import { useState, useEffect, useCallback } from 'react';
import { BookCard } from '../components/BookCard';
import { SearchBar } from '../components/SearchBar';
import { Skeleton } from '../components/ui/skeleton';
import { BookOpen, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useGeneration } from '../contexts/GenerationContext';
import { apiUrl } from '../config/api';

export default function Home({ onSelectBook, onShowDetails }) {
  const { user, token } = useAuth();
  const [books, setBooks] = useState([]);
  const [libraryBooks, setLibraryBooks] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search state lifted up from SearchBar
  const [searchState, setSearchState] = useState({ query: '', results: [], isSearching: false });
  const isSearchActive = searchState.query.trim().length > 0;

  // Update local libraryBooks set when a BookCard's saved state changes
  const handleSavedChanged = (bookId, isSaved) => {
    setLibraryBooks(prev => {
      const next = new Set(prev);
      if (isSaved) next.add(bookId);
      else next.delete(bookId);
      return next;
    });
  };

  // When user selects a Gutenberg-only result, create it in our DB first
  const ensureBookInDb = async (gutenbergBook) => {
    try {
      const response = await fetch(apiUrl('/api/books/from-gutenberg'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gutenberg_id: gutenbergBook.gutenberg_id,
          title: gutenbergBook.title,
          author: gutenbergBook.author,
          language: gutenbergBook.language || 'en'
        })
      });
      if (response.ok) return await response.json();
    } catch (error) {
      console.error('[Home] Failed to create book from Gutenberg:', error);
    }
    return null;
  };

  const handleSearchSelectBook = async (book) => {
    if (book && book._isGutenberg) {
      const dbBook = await ensureBookInDb(book);
      if (dbBook) onSelectBook(dbBook.id);
    } else {
      onSelectBook(typeof book === 'object' ? book.id : book);
    }
  };

  const handleSearchShowDetails = async (book) => {
    if (book && book._isGutenberg) {
      const dbBook = await ensureBookInDb(book);
      if (dbBook) onShowDetails(dbBook);
    } else {
      onShowDetails(book);
    }
  };

  const { generations } = useGeneration();

  const fetchData = useCallback(async () => {
    try {
      const booksResponse = await fetch(apiUrl('/api/books'));
      if (!booksResponse.ok) {
        throw new Error(`Failed to load books (${booksResponse.status})`);
      }
      const booksData = await booksResponse.json();
      setBooks(booksData);

      if (user && token) {
        try {
          const libraryResponse = await fetch(apiUrl('/api/library'), {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (libraryResponse.ok) {
            const libraryData = await libraryResponse.json();
            setLibraryBooks(new Set(libraryData.books?.map(b => b.book_id) || []));
          }
        } catch (libraryErr) {
          console.error('[Home] Failed to load library:', libraryErr);
        }
      }
      setError(null);
    } catch (err) {
      console.error('[Home] ERROR:', err.message);
      setError(err.message);
      setBooks([]);
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  // Initial fetch
  useEffect(() => { fetchData(); }, [fetchData]);

  // Refetch when any generation completes — updates book.status in parent state
  // so BookCard shows "Read Now" instead of "Add to Library"
  useEffect(() => {
    const hasCompletion = Object.values(generations).some(g => g.status === 'complete');
    if (hasCompletion) {
      fetchData();
    }
  }, [generations, fetchData]);

  // Also refetch on window focus (catches completion if user tabbed away)
  useEffect(() => {
    const onFocus = () => fetchData();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchData]);

  // Books to render: search results when searching, otherwise the catalog
  const displayBooks = isSearchActive ? searchState.results : books.slice(0, 20);

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="mb-8 pb-4 border-b">
        <h1 className="text-2xl font-bold tracking-tight font-serif mb-1">
          welcome to readwell
        </h1>
        <p className="text-sm text-muted-foreground">
          reading for everyone
        </p>
      </div>

      {/* Section header + search input */}
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h2 className="text-2xl font-semibold">
          {isSearchActive
            ? `Search results${searchState.isSearching ? '' : ` (${searchState.results.length})`}`
            : 'Discover'}
        </h2>
        <div className="flex-shrink-0">
          <SearchBar onSearchStateChange={setSearchState} />
        </div>
      </div>

      {/* Book grid - same dimensions whether showing catalog or search results */}
      {loading && !isSearchActive ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="space-y-3">
              <Skeleton className="h-48 w-full rounded-lg" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ))}
        </div>
      ) : error && !isSearchActive ? (
        <div className="text-center py-12 border-2 border-destructive rounded-lg bg-destructive/10">
          <AlertCircle className="h-16 w-16 mx-auto text-destructive mb-4" />
          <h3 className="text-xl font-semibold mb-2 text-destructive">Failed to Load Books</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
        </div>
      ) : isSearchActive && searchState.isSearching && searchState.results.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-3">
              <Skeleton className="h-48 w-full rounded-lg" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ))}
        </div>
      ) : isSearchActive && searchState.results.length === 0 && !searchState.isSearching ? (
        <div className="text-center py-12 border border-dashed rounded-lg">
          <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-3 opacity-50" />
          <p className="text-muted-foreground">No books found for "{searchState.query}"</p>
        </div>
      ) : displayBooks.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {displayBooks.map(book => {
            const key = book._isGutenberg ? `g-${book.gutenberg_id}` : book.id;
            return (
              <BookCard
                key={key}
                book={book}
                onSelectBook={isSearchActive ? () => handleSearchSelectBook(book) : onSelectBook}
                onShowDetails={isSearchActive ? () => handleSearchShowDetails(book) : onShowDetails}
                isSaved={libraryBooks.has(book.id)}
                skipLibraryCheck={true}
                onSavedChanged={handleSavedChanged}
              />
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <BookOpen className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Books Available Yet</h3>
          <p className="text-muted-foreground">
            Books will be generated on-demand when students request them.
          </p>
        </div>
      )}
    </div>
  );
}
